import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Upload, Edit, Trash2, Search, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined categories
const CATEGORIES = [
  'القسم ١: مفاهيم وتعريفات القياس',
  'القسم ٢: إجراءات جمع البيانات',
  'القسم ٣: نزاهة البيانات واتفاقية بين المراقبين',
  'القسم ٤: عرض البيانات وتمثيلها بيانيًا',
  'القسم ٥: تحليل البيانات وتفسيرها',
  'القسم ٦: التقييم المتقدم (مستوى تحليل الجودة السلوكي)'
];

const QuestionBank = () => {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [extractedQuestions, setExtractedQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  const [formData, setFormData] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_answer: 'A',
    category: '',
    difficulty: 'medium',
    level: 'ABAT'
  });

  useEffect(() => {
    fetchQuestions();
  }, [categoryFilter, difficultyFilter, levelFilter, searchTerm]);

  const fetchQuestions = async () => {
    try {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      if (difficultyFilter) params.difficulty = difficultyFilter;
      if (levelFilter) params.level = levelFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await axios.get(`${API}/questions`, { params });
      setQuestions(response.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingQuestion) {
        await axios.put(`${API}/questions/${editingQuestion.id}`, formData);
        toast.success(t('updateSuccess'));
      } else {
        await axios.post(`${API}/questions`, formData);
        toast.success(t('questionAdded'));
      }
      
      setShowAddDialog(false);
      setEditingQuestion(null);
      setFormData({
        question_text: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct_answer: 'A',
        category: '',
        difficulty: 'medium'
      });
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDelete = async (questionId) => {
    try {
      await axios.delete(`${API}/questions/${questionId}`);
      toast.success(t('deleteSuccess'));
      fetchQuestions();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/questions/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Now all file types go through review process
      setExtractedQuestions(response.data.questions);
      setSelectedQuestions(new Set(response.data.questions.map((_, idx) => idx)));
      setShowUploadDialog(false);
      setShowReviewDialog(true);
      setFile(null);
      toast.success(`${t('extracted')} ${response.data.count} ${t('questionsCount')}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentExtraction = async () => {
    if (!documentFile) {
      toast.error('Please select a document file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', documentFile);

      const response = await axios.post(`${API}/questions/extract-from-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setExtractedQuestions(response.data.questions);
      setSelectedQuestions(new Set(response.data.questions.map((_, idx) => idx)));
      setShowDocumentDialog(false);
      setShowReviewDialog(true);
      toast.success(`${t('extracted')} ${response.data.count} ${t('questionsCount')}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setUploading(false);
    }
  };

  const handleAddSelectedQuestions = async () => {
    const questionsToAdd = extractedQuestions.filter((_, idx) => selectedQuestions.has(idx));
    
    if (questionsToAdd.length === 0) {
      toast.error('Please select at least one question');
      return;
    }

    try {
      let addedCount = 0;
      for (const q of questionsToAdd) {
        await axios.post(`${API}/questions`, {
          question_text: q.question_text,
          option_a: q.option_a,
          option_b: q.option_b,
          option_c: q.option_c,
          option_d: q.option_d,
          correct_answer: q.correct_answer,
          category: q.category,
          difficulty: q.difficulty
        });
        addedCount++;
      }
      
      toast.success(`${addedCount} ${t('questionsCount')} ${t('questionAdded')}`);
      setShowReviewDialog(false);
      setExtractedQuestions([]);
      setSelectedQuestions(new Set());
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const toggleQuestionSelection = (idx) => {
    const newSelection = new Set(selectedQuestions);
    if (newSelection.has(idx)) {
      newSelection.delete(idx);
    } else {
      newSelection.add(idx);
    }
    setSelectedQuestions(newSelection);
  };

  const selectAllQuestions = () => {
    setSelectedQuestions(new Set(extractedQuestions.map((_, idx) => idx)));
  };

  const deselectAllQuestions = () => {
    setSelectedQuestions(new Set());
  };

  const updateExtractedQuestion = (idx, field, value) => {
    const updated = [...extractedQuestions];
    updated[idx][field] = value;
    setExtractedQuestions(updated);
  };

  const startEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      question_text: question.question_text,
      option_a: question.option_a,
      option_b: question.option_b,
      option_c: question.option_c,
      option_d: question.option_d,
      correct_answer: question.correct_answer,
      category: question.category || '',
      difficulty: question.difficulty
    });
    setShowAddDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="question-bank">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">{t('questionBank')}</h2>
          <p className="text-gray-600 mt-1">{questions.length} {t('questionsCount')}</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
            <DialogTrigger asChild>
              <Button data-testid="upload-document-button" className="bg-gradient-to-r from-purple-600 to-pink-600">
                <Upload className="h-4 w-4 mr-2" />
                {t('uploadDocument')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('uploadDocument')}</DialogTitle>
                <DialogDescription>Word (.docx) or PDF files</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
                  <input
                    data-testid="document-upload-input"
                    type="file"
                    accept=".docx,.doc,.pdf"
                    onChange={(e) => setDocumentFile(e.target.files[0])}
                    className="hidden"
                    id="document-upload"
                  />
                  <label htmlFor="document-upload" className="cursor-pointer block">
                    <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-sm text-gray-600 mb-2">Upload Word or PDF document</p>
                    {documentFile && (
                      <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-sm font-medium text-purple-700">✓ {documentFile.name}</p>
                      </div>
                    )}
                  </label>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDocumentDialog(false);
                      setDocumentFile(null);
                    }} 
                    className="flex-1"
                  >
                    {t('cancel')}
                  </Button>
                  <Button 
                    data-testid="extract-button" 
                    onClick={handleDocumentExtraction} 
                    disabled={!documentFile || uploading} 
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('loading')}
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {t('extractQuestions')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button data-testid="upload-questions-button" className="bg-gradient-to-r from-green-600 to-emerald-600">
                <Upload className="h-4 w-4 mr-2" />
                {t('uploadQuestions')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('uploadQuestions')}</DialogTitle>
                <DialogDescription>{t('supportsExcelCSV')}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-500 transition-colors">
                  <input
                    data-testid="file-upload-input"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer block">
                    <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-sm text-gray-600 mb-2">{t('dragDropFile')}</p>
                    {file && (
                      <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm font-medium text-green-700">✓ {file.name}</p>
                      </div>
                    )}
                  </label>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowUploadDialog(false);
                      setFile(null);
                    }} 
                    className="flex-1"
                  >
                    {t('cancel')}
                  </Button>
                  <Button 
                    data-testid="upload-file-button" 
                    onClick={handleFileUpload} 
                    disabled={!file || uploading} 
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('loading')}
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {t('uploadFile')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) {
              setEditingQuestion(null);
              setFormData({
                question_text: '',
                option_a: '',
                option_b: '',
                option_c: '',
                option_d: '',
                correct_answer: 'A',
                category: '',
                difficulty: 'medium',
                level: 'ABAT'
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-question-button" className="bg-gradient-to-r from-indigo-600 to-cyan-600">
                <Plus className="h-4 w-4 mr-2" />
                {t('addQuestion')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingQuestion ? t('edit') : t('addQuestion')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('questionText')}</Label>
                  <Textarea
                    data-testid="question-text-input"
                    value={formData.question_text}
                    onChange={(e) => setFormData({...formData, question_text: e.target.value})}
                    required
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('optionA')}</Label>
                    <Input
                      data-testid="option-a-input"
                      value={formData.option_a}
                      onChange={(e) => setFormData({...formData, option_a: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('optionB')}</Label>
                    <Input
                      data-testid="option-b-input"
                      value={formData.option_b}
                      onChange={(e) => setFormData({...formData, option_b: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('optionC')}</Label>
                    <Input
                      data-testid="option-c-input"
                      value={formData.option_c}
                      onChange={(e) => setFormData({...formData, option_c: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('optionD')}</Label>
                    <Input
                      data-testid="option-d-input"
                      value={formData.option_d}
                      onChange={(e) => setFormData({...formData, option_d: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>{t('correctAnswer')}</Label>
                    <Select
                      value={formData.correct_answer}
                      onValueChange={(value) => setFormData({...formData, correct_answer: value})}
                    >
                      <SelectTrigger data-testid="correct-answer-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                        <SelectItem value="D">D</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('category')}</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({...formData, category: value})}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder={t('category')} />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('difficulty')}</Label>
                    <Select
                      value={formData.difficulty}
                      onValueChange={(value) => setFormData({...formData, difficulty: value})}
                    >
                      <SelectTrigger data-testid="difficulty-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">{t('easy')}</SelectItem>
                        <SelectItem value="medium">{t('medium')}</SelectItem>
                        <SelectItem value="hard">{t('hard')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('level') || 'Level'}</Label>
                    <Select
                      value={formData.level || 'ABAT'}
                      onValueChange={(value) => setFormData({...formData, level: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ABAT">ABAT</SelectItem>
                        <SelectItem value="QASP-S">QASP-S</SelectItem>
                        <SelectItem value="QBA">QBA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                    {t('cancel')}
                  </Button>
                  <Button data-testid="save-question-button" type="submit">{t('save')}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="shadow-md">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="search-input"
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter || "all"} onValueChange={(val) => setCategoryFilter(val === "all" ? "" : val)}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder={t('category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('categories')}</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={difficultyFilter || "all"} onValueChange={(val) => setDifficultyFilter(val === "all" ? "" : val)}>
              <SelectTrigger data-testid="difficulty-filter">
                <SelectValue placeholder={t('difficulty')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('difficulty')}</SelectItem>
                <SelectItem value="easy">{t('easy')}</SelectItem>
                <SelectItem value="medium">{t('medium')}</SelectItem>
                <SelectItem value="hard">{t('hard')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={levelFilter || "all"} onValueChange={(val) => setLevelFilter(val === "all" ? "" : val)}>
              <SelectTrigger>
                <SelectValue placeholder={t('level') || 'Level'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allLevels') || 'All Levels'}</SelectItem>
                <SelectItem value="ABAT">ABAT</SelectItem>
                <SelectItem value="QASP-S">QASP-S</SelectItem>
                <SelectItem value="QBA">QBA</SelectItem>
                <SelectItem value="QASP-S,ABAT">QASP-S + ABAT</SelectItem>
                <SelectItem value="QASP-S,QBA,ABAT">QASP-S + QBA + ABAT</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Questions List */}
      <div className="space-y-4">
        {questions.length === 0 ? (
          <Card className="shadow-md">
            <CardContent className="pt-6 text-center text-gray-500">
              {t('noData')}
            </CardContent>
          </Card>
        ) : (
          questions.map((question, index) => (
            <Card key={question.id} className="shadow-md card-hover">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-bold text-indigo-600">#{index + 1}</span>
                      {question.category && (
                        <span className="badge badge-info">{question.category}</span>
                      )}
                      <span className={`badge ${
                        question.difficulty === 'easy' ? 'badge-success' :
                        question.difficulty === 'hard' ? 'badge-danger' : 'badge-warning'
                      }`}>
                        {t(question.difficulty)}
                      </span>
                      {question.level && (
                        <span className="badge bg-purple-100 text-purple-700 border-purple-300">
                          {question.level}
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-gray-900 mb-3">{question.question_text}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {['A', 'B', 'C', 'D'].map(opt => (
                        <div
                          key={opt}
                          className={`p-3 rounded-lg border ${
                            question.correct_answer === opt
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <span className="font-semibold mr-2">{opt}.</span>
                          {question[`option_${opt.toLowerCase()}`]}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`edit-question-${index}`}
                      size="icon"
                      variant="outline"
                      onClick={() => startEdit(question)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button data-testid={`delete-question-${index}`} size="icon" variant="outline" className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(question.id)}>
                            {t('delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Review Extracted Questions Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('reviewQuestions')}</DialogTitle>
            <DialogDescription>
              {selectedQuestions.size} of {extractedQuestions.length} {t('questionsCount')} selected
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={selectAllQuestions} variant="outline" size="sm">
                {t('selectAll')}
              </Button>
              <Button onClick={deselectAllQuestions} variant="outline" size="sm">
                {t('deselectAll')}
              </Button>
            </div>

            {extractedQuestions.map((q, idx) => (
              <Card key={idx} className={`${selectedQuestions.has(idx) ? 'border-2 border-indigo-500' : ''}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      checked={selectedQuestions.has(idx)}
                      onChange={() => toggleQuestionSelection(idx)}
                      className="mt-1 h-5 w-5"
                    />
                    <div className="flex-1 space-y-3">
                      <div>
                        <Label className="text-xs text-gray-500">Question {idx + 1}</Label>
                        <Textarea
                          value={q.question_text}
                          onChange={(e) => updateExtractedQuestion(idx, 'question_text', e.target.value)}
                          className="mt-1 min-h-[100px]"
                          rows={4}
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">A.</Label>
                          <Textarea
                            value={q.option_a}
                            onChange={(e) => updateExtractedQuestion(idx, 'option_a', e.target.value)}
                            className="min-h-[60px]"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">B.</Label>
                          <Textarea
                            value={q.option_b}
                            onChange={(e) => updateExtractedQuestion(idx, 'option_b', e.target.value)}
                            className="min-h-[60px]"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">C.</Label>
                          <Textarea
                            value={q.option_c}
                            onChange={(e) => updateExtractedQuestion(idx, 'option_c', e.target.value)}
                            className="min-h-[60px]"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">D.</Label>
                          <Textarea
                            value={q.option_d}
                            onChange={(e) => updateExtractedQuestion(idx, 'option_d', e.target.value)}
                            className="min-h-[60px]"
                            rows={2}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-2">
                        <Select
                          value={q.correct_answer}
                          onValueChange={(value) => updateExtractedQuestion(idx, 'correct_answer', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('correctAnswer')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="A">A</SelectItem>
                            <SelectItem value="B">B</SelectItem>
                            <SelectItem value="C">C</SelectItem>
                            <SelectItem value="D">D</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Select
                          value={q.category}
                          onValueChange={(value) => updateExtractedQuestion(idx, 'category', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('category')} />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Select
                          value={q.difficulty}
                          onValueChange={(value) => updateExtractedQuestion(idx, 'difficulty', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('difficulty')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="easy">{t('easy')}</SelectItem>
                            <SelectItem value="medium">{t('medium')}</SelectItem>
                            <SelectItem value="hard">{t('hard')}</SelectItem>
                          </SelectContent>
                        </Select>

                        <Select
                          value={q.level || 'ABAT'}
                          onValueChange={(value) => updateExtractedQuestion(idx, 'level', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('level') || 'Level'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ABAT">ABAT</SelectItem>
                            <SelectItem value="QASP-S">QASP-S</SelectItem>
                            <SelectItem value="QBA">QBA</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleAddSelectedQuestions}
                className="bg-gradient-to-r from-indigo-600 to-cyan-600"
              >
                {t('addSelectedQuestions')} ({selectedQuestions.size})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionBank;
